import { Fragment } from 'react';
import { Box, Group, Text, Tooltip } from '@mantine/core';
import { heatmapCells, type HeatmapCell } from './heatmap';
import { VERDICT_META, verdictMeta } from './labels';
import type { Verdict } from './types';

/** A plan change to mark on the grid. The CELLS ARE HOURS, so the marker uses
 *  them: a change at 14:20 gets a tick at the 14:00 boundary of that day's
 *  row — cells left of it ran the old plan, right of it the new. The hour cell
 *  is a rendering grain, not a claim of accuracy: `at` carries the real
 *  timestamp and the tooltip names it. */
export interface SpeedChangeMarker {
  /** 0-6, same orientation as the grid rows (oldest first, row 6 = today). */
  dayIndex: number;
  /** 0-23, site-local — the hour cell the change falls in. */
  hour: number;
  downloadMbps: number;
  uploadMbps: number;
  /** Relative to the previous plan: 'up' = faster, 'down' = slower. */
  direction: 'up' | 'down';
  /** ISO timestamp of the actual change. */
  at: string;
}

export interface CapacityHeatmapProps {
  /** 7 local days x 24 hours, oldest first, row 6 = today: saturated minutes. */
  grid: number[][];
  /** Same shape: minutes that cleared the packet floor. The denominator. */
  measuredGrid: number[][];
  /** Same shape: the backend's verdict per cell. */
  cellVerdicts: Verdict[][];
  /** Same shape: minutes sampled before the packet floor. Optional so an older
   *  payload still renders (every unmeasured cell then reads as unsampled, the
   *  previous behaviour). */
  sampledGrid?: number[][];
  /** Plan changes inside the window, marked as a tick at the exact hour within
   *  that day's row plus a chip naming the new speed. Optional per the
   *  `sampledGrid` convention, so an older payload still renders. */
  speedChanges?: SpeedChangeMarker[];
  /** The plan in effect when the window opened — rendered as a LABEL above the
   *  grid, deliberately never a tick: a window-start period knows WHAT applied
   *  but not when it began, and a tick would claim a moment never recorded. */
  windowStartPlan?: { downloadMbps: number; uploadMbps: number } | null;
  /** Same shape as `grid`, split by direction: minutes saturated downstream.
   *  With all four direction props present every judged cell is drawn as two
   *  triangles — download top-left, upload bottom-right — each coloured by its
   *  own backend verdict. Any one missing (member portal, older backend) and
   *  the grid renders exactly as before, one colour per cell. */
  gridDown?: number[][];
  /** Upload twin of `gridDown`. */
  gridUp?: number[][];
  /** The backend's per-direction verdict per cell, downstream. */
  cellVerdictsDown?: Verdict[][];
  /** Upload twin of `cellVerdictsDown`. */
  cellVerdictsUp?: Verdict[][];
}

const DAY_LABELS = ['6d ago', '5d ago', '4d ago', '3d ago', '2d ago', 'Yesterday', 'Today'];

/**
 * Colour comes from the SAME map the badges use, keyed on the backend's own
 * per-cell verdict — so a cell can never disagree with the badge above it.
 *
 * The previous version had two branches: any saturation was red, everything
 * else was one grey. "Measured and perfectly healthy" and "never sampled" were
 * therefore indistinguishable, and a network with 128 healthy minutes rendered
 * as an entirely grey week — which reads as broken monitoring, not a healthy
 * network.
 *
 * Opacity, and only opacity, is computed here: it scales with the cell's own
 * share so two networks are comparable. The old `count / max` made shading
 * depend on how bad the window's worst cell happened to be.
 */
/** The two non-verdict states a cell can be in, kept apart on purpose. */
type CellState = Verdict | 'idle' | 'no_data';

/**
 * An hour with samples but no measurable traffic is IDLE — a fact about the
 * network. An hour with no samples at all is missing monitoring. Painting both
 * the same made a normal office week (four fifths idle) look like a broken
 * sampler, which is the same class of bug `measured_grid` was added to fix one
 * level up.
 */
const cellState = (cell: { verdict: Verdict; measured: number; sampled: number }): CellState => {
  if (cell.measured > 0) return cell.verdict;
  return cell.sampled > 0 ? 'idle' : 'no_data';
};

/** The neutral the quiet states are built from. */
const QUIET = 'var(--mantine-color-default-border)';

const cellStyle = (verdict: CellState, share: number) => {
  if (verdict === 'no_data') {
    // Genuinely nothing measured here, and drawn as ABSENCE: no fill at all,
    // just a hairline box with a faint hatch through it (#341).
    //
    // It used to be `--mantine-color-default-hover` against idle's
    // `--mantine-color-default-border` — two tokens that land within a couple
    // of RGB points of each other in dark mode, so cells and, worse, the legend
    // showed one grey for two different facts. Chasing a third grey only moves
    // that problem; a difference in TREATMENT (unfilled + hatched vs filled)
    // survives both colour schemes and any branded theme, because it does not
    // depend on two neutrals staying far enough apart.
    return {
      backgroundColor: 'transparent',
      backgroundImage: `repeating-linear-gradient(45deg, ${QUIET} 0 1px, transparent 1px 3px)`,
      boxShadow: `inset 0 0 0 1px ${QUIET}`,
    };
  }
  if (verdict === 'idle') {
    // Sampled, nothing to judge. Distinct from no_data but still quiet — it is
    // the normal state of most hours and must not read as a problem.
    return { backgroundColor: QUIET };
  }
  if (verdict === 'insufficient_data') {
    // Measured, but too few minutes to band. One step up the ramp from idle:
    // `gray-6` is a fixed mid-grey in both schemes, where the `default-*`
    // neutrals flip with the scheme, so it stays separable from idle either way
    // rather than inheriting the collision just fixed above.
    return { backgroundColor: 'var(--mantine-color-gray-6)', opacity: 0.6 };
  }
  const color = verdictMeta(verdict).color;
  if (verdict === 'healthy') {
    return { backgroundColor: `var(--mantine-color-${color}-6)` };
  }
  return {
    backgroundColor: `var(--mantine-color-${color}-6)`,
    opacity: 0.45 + 0.55 * Math.min(1, share),
  };
};

const LEGEND: { state: CellState; label: string }[] = [
  { state: 'no_data', label: 'No data' },
  { state: 'idle', label: 'Idle' },
  // Reachable and previously unlegended: `cellState` returns the cell's own
  // verdict whenever `measured > 0`, and `_cell_verdict` returns
  // insufficient_data below GRID_CELL_MIN_MINUTES — so a cell with 1..N-1
  // measured minutes painted a grey nothing in the legend explained.
  { state: 'insufficient_data', label: VERDICT_META.insufficient_data.label },
  { state: 'healthy', label: VERDICT_META.healthy.label },
  { state: 'occasionally_at_capacity', label: VERDICT_META.occasionally_at_capacity.label },
  { state: 'frequently_at_capacity', label: VERDICT_META.frequently_at_capacity.label },
];

/** Left edge of the tick for a change in hour `h` — the boundary at the start
 *  of that hour's cell, as a fraction of the row width. */
const tickLeft = (hour: number) => `calc(${((hour / 24) * 100).toFixed(3)}% - 1px)`;

const changeLabel = (c: SpeedChangeMarker) =>
  `Plan changed to ${c.downloadMbps} / ${c.uploadMbps} Mbit/s at ${new Date(c.at).toLocaleString()}`;

/** Download owns the top-left triangle, upload the bottom-right. Both meet on
 *  the top-right → bottom-left diagonal. */
const TRIANGLE_DOWN = 'polygon(0 0, 100% 0, 0 100%)';
const TRIANGLE_UP = 'polygon(100% 0, 100% 100%, 0 100%)';

/** A hairline in the page's own background along that diagonal, so two
 *  triangles of the SAME colour (healthy both ways) still read as a split cell
 *  rather than one solid one. `--mantine-color-body` follows the scheme. */
const SPLIT_DIVIDER =
  'linear-gradient(to bottom right, transparent calc(50% - 0.5px), var(--mantine-color-body) calc(50% - 0.5px), var(--mantine-color-body) calc(50% + 0.5px), transparent calc(50% + 0.5px))';

/** Whether a cell is drawn as two triangles: it has both directions AND it is
 *  a banded verdict. Idle / unsampled / too-few-minutes cells say nothing
 *  per direction (same denominator), so they keep their single-colour look. */
const isSplit = (cell: HeatmapCell, state: CellState) =>
  cell.down !== undefined &&
  cell.up !== undefined &&
  state !== 'no_data' &&
  state !== 'idle' &&
  state !== 'insufficient_data';

/**
 * The tooltip sentence for one cell, prefixed with its row label.
 *
 * Exported because it is the one place the split is put into words — a
 * triangle's colour alone is not something a touch user or a screen reader can
 * read, and it is what the tests pin.
 */
export const heatmapCellLabel = (cell: HeatmapCell, dayLabel: string): string => {
  const state = cellState(cell);
  const at = `${dayLabel} ${String(cell.hour).padStart(2, '0')}:00`;
  if (state === 'no_data') return `${at} — no samples`;
  // Say WHY it is not judged. "No data" on an hour the sampler covered fine
  // sends people looking for a monitoring fault that does not exist.
  if (state === 'idle') return `${at} — idle (${cell.sampled} min sampled, none with enough traffic to judge)`;
  if (state === 'insufficient_data') return `${at} — only ${cell.measured} measured min, too few to judge`;
  if (isSplit(cell, state)) {
    return `${at} · Download at cap ${cell.down!.count}/${cell.measured} min · Upload at cap ${cell.up!.count}/${cell.measured} min`;
  }
  return `${at} — ${cell.count} of ${cell.measured} measured min at capacity`;
};

/** Legend key for the split: a hairline box with only that direction's
 *  triangle filled. */
const DirectionKey = ({ direction, label }: { direction: 'down' | 'up'; label: string }) => (
  <Group gap={6}>
    <Box
      pos="relative"
      w={10}
      h={10}
      style={{ borderRadius: 2, overflow: 'hidden', boxShadow: `inset 0 0 0 1px ${QUIET}` }}
    >
      <Box
        data-direction={direction}
        pos="absolute"
        style={{
          inset: 0,
          clipPath: direction === 'down' ? TRIANGLE_DOWN : TRIANGLE_UP,
          backgroundColor: 'var(--mantine-color-dimmed)',
        }}
      />
    </Box>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
  </Group>
);

/**
 * A CSS grid of Mantine Boxes — deliberately NOT recharts (no heatmap
 * primitive there, and the two frontends sit on different recharts majors).
 */
export const CapacityHeatmap = ({
  grid,
  measuredGrid,
  cellVerdicts,
  sampledGrid,
  speedChanges,
  windowStartPlan,
  gridDown,
  gridUp,
  cellVerdictsDown,
  cellVerdictsUp,
}: CapacityHeatmapProps) => {
  // All four or none: a payload carrying half of them would otherwise paint one
  // triangle from data and the other from a default.
  const directions =
    gridDown && gridUp && cellVerdictsDown && cellVerdictsUp
      ? { gridDown, gridUp, cellVerdictsDown, cellVerdictsUp }
      : undefined;
  const cells = heatmapCells(grid, measuredGrid, cellVerdicts, sampledGrid, directions);
  const changes = speedChanges ?? [];
  // The chip column only exists when there is something to put in it, so a
  // grid without changes renders pixel-identical to the previous version.
  const hasChanges = changes.length > 0;
  return (
    <Box>
      {windowStartPlan && (
        <Text size="xs" c="dimmed" mb={6}>
          In effect at the start of the window:{' '}
          <Text component="span" size="xs" fw={600}>
            {windowStartPlan.downloadMbps} / {windowStartPlan.uploadMbps} Mbit/s
          </Text>
        </Text>
      )}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: hasChanges ? 'max-content 1fr max-content' : 'max-content 1fr',
          gap: 2,
          columnGap: 6,
          alignItems: 'center',
        }}
      >
        {DAY_LABELS.map((label, day) => {
          const rowChanges = changes.filter((c) => c.dayIndex === day);
          // A faint continuation of the previous row's tick, so "everything
          // after this is the new plan" reads without re-checking the chip.
          const continuations = changes.filter((c) => c.dayIndex === day - 1);
          const rowCells = cells
            .filter((c) => c.day === day)
            .map((cell) => {
                const state = cellState(cell);
                const cellLabel = heatmapCellLabel(cell, label);
                if (isSplit(cell, state)) {
                  // Each triangle is coloured by ITS OWN backend verdict and
                  // shaded by its own share, through the same `cellStyle` a
                  // whole cell uses — one colour scale, applied per direction.
                  const down = cell.down!;
                  const up = cell.up!;
                  return (
                    <Tooltip key={`${cell.day}-${cell.hour}`} label={cellLabel}>
                      <Box
                        pos="relative"
                        aria-label={cellLabel}
                        style={{ aspectRatio: '1', borderRadius: 2, overflow: 'hidden' }}
                      >
                        <Box
                          data-direction="down"
                          pos="absolute"
                          style={{ inset: 0, clipPath: TRIANGLE_DOWN, ...cellStyle(down.verdict, down.share) }}
                        />
                        <Box
                          data-direction="up"
                          pos="absolute"
                          style={{ inset: 0, clipPath: TRIANGLE_UP, ...cellStyle(up.verdict, up.share) }}
                        />
                        <Box
                          pos="absolute"
                          style={{ inset: 0, backgroundImage: SPLIT_DIVIDER, pointerEvents: 'none' }}
                        />
                      </Box>
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip key={`${cell.day}-${cell.hour}`} label={cellLabel}>
                    <Box
                      style={{
                        aspectRatio: '1',
                        borderRadius: 2,
                        ...cellStyle(state, cell.share),
                      }}
                    />
                  </Tooltip>
                );
              });
          return (
            <Fragment key={day}>
              <Text size="xs" c="dimmed" pr={6} style={{ whiteSpace: 'nowrap' }}>
                {label}
              </Text>
              <Box
                style={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(24, minmax(8px, 1fr))',
                  gap: 2,
                }}
              >
                {rowCells}
                {rowChanges.map((c) => (
                  <Tooltip key={c.at} label={changeLabel(c)}>
                    <Box
                      style={{
                        position: 'absolute',
                        top: -2,
                        bottom: -2,
                        left: tickLeft(c.hour),
                        width: 2,
                        borderRadius: 1,
                        backgroundColor: 'var(--mantine-primary-color-filled)',
                      }}
                    />
                  </Tooltip>
                ))}
                {continuations.map((c) => (
                  <Box
                    key={`cont-${c.at}`}
                    style={{
                      position: 'absolute',
                      top: -1,
                      bottom: -3,
                      left: tickLeft(c.hour),
                      width: 2,
                      borderRadius: 1,
                      backgroundColor: 'var(--mantine-primary-color-filled)',
                      opacity: 0.35,
                      pointerEvents: 'none',
                    }}
                  />
                ))}
              </Box>
              {hasChanges && (
                <Group gap={4} wrap="nowrap">
                  {rowChanges.map((c) => (
                    <Tooltip key={`chip-${c.at}`} label={changeLabel(c)}>
                      <Text
                        component="span"
                        size="xs"
                        fw={600}
                        c="var(--mantine-primary-color-filled)"
                        style={{
                          border: '1px solid var(--mantine-primary-color-filled)',
                          borderRadius: 999,
                          padding: '0 7px',
                          whiteSpace: 'nowrap',
                          backgroundColor: 'var(--mantine-primary-color-light)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {c.direction === 'up' ? '↑' : '↓'} {c.downloadMbps} / {c.uploadMbps}
                      </Text>
                    </Tooltip>
                  ))}
                </Group>
              )}
            </Fragment>
          );
        })}
      </Box>
      <Group gap="md" mt="xs">
        {LEGEND.map(({ state, label }) => (
          <Group key={state} gap={6}>
            <Box
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                ...cellStyle(state, 1),
              }}
            />
            <Text size="xs" c="dimmed">
              {label}
            </Text>
          </Group>
        ))}
        {directions && (
          // Only when cells are actually split: on a payload without the
          // per-direction grids this key would explain a shape never drawn.
          <Group gap="sm">
            <DirectionKey direction="down" label="Download" />
            <DirectionKey direction="up" label="Upload" />
          </Group>
        )}
      </Group>
    </Box>
  );
};
